<script setup lang="ts">
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { wineService } from '@/services/wine'
import { Review } from '@/types'

// Props
interface Props {
    review: Review
    showUser?: boolean
    showActions?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    showUser: true,
    showActions: false,
})

// Emits
const emit = defineEmits<{
    edit: [review: Review]
    delete: [review: Review]
}>()

const authStore = useAuthStore()

// Computed
const canEdit = computed(() => {
    return authStore.user?.id === props.review.user_id
})

// Methods
const editReview = () => {
    emit('edit', props.review)
}

const deleteReview = () => {
    emit('delete', props.review)
}

const getRatingStars = (rating: number): string => {
    return wineService.getRatingStars(Math.round(rating))
}

const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ru-RU')
}

const handleImageError = (event: Event) => {
    const img = event.target as HTMLImageElement
    img.src = '/images/user-placeholder.jpg'
}
</script>

<template>
    <div class="review-card">
        <div class="review-header">
            <div class="review-user">
                <div v-if="showUser" class="user-avatar">
                    <img
                        v-if="review.user?.photo_url"
                        :src="review.user.photo_url"
                        :alt="review.user.first_name"
                        @error="handleImageError"
                    />
                    <div v-else class="avatar-placeholder">
                        {{ review.user?.first_name?.charAt(0) || 'U' }}
                    </div>
                </div>

                <div class="user-info">
                    <h4 class="user-name">
                        {{ review.user?.first_name }} {{ review.user?.last_name }}
                    </h4>
                    <span class="review-date">{{ formatDate(review.created_at) }}</span>
                </div>
            </div>

            <div class="review-rating">
                <span class="rating-stars">{{ getRatingStars(review.rating) }}</span>
                <span class="rating-value">{{ review.rating }}/5</span>
            </div>
        </div>

        <div v-if="review.comment" class="review-comment">
            <p>{{ review.comment }}</p>
        </div>

        <div v-if="review.place" class="review-place">
            <span class="place-icon">📍</span>
            <span>{{ review.place }}</span>
        </div>

        <div v-if="showActions && canEdit" class="review-actions">
            <button class="btn btn-outline btn-sm" @click="editReview">Редактировать</button>
            <button class="btn btn-outline btn-sm" @click="deleteReview">Удалить</button>
        </div>
    </div>
</template>
